import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { order } from '@triplabo/web/services';
import { showToast } from '@triplabo/web/components';
import { isHardware } from '@triplabo/web/utils';
import CancelOrder from './CancelOrder';
import CancelReason from './CancelReason';
import styles from './cancel.module.css';

interface CancelOrderProps {
  onClose: () => void;
  onCancel: () => void;
  orderId: string;
  show: boolean;
  cancelFee?: number;
}

const STEP = {
  confirm: 0,
  reason: 1
};

type STEP_TYPE = (typeof STEP)[keyof typeof STEP];

const Cancel: FC<CancelOrderProps> = observer(
  ({ onClose, onCancel, orderId, show, cancelFee }) => {
    const { t } = useTranslation();

    const [step, setStep] = useState<STEP_TYPE>(STEP.confirm);
    const [selectedReason, setSelectedReason] = useState<string>('');
    const onConfirm = useCallback(
      async (reason?: string) => {
        try {
          const res = await order.cancelOrder({
            orderId,
            cancellationReason: reason
          });
          if (res) {
            showToast({
              message: t('views.cancelOrder.success')
            });
            onCancel();
          }
        } catch (error) {
          console.error(error);
        }
      },
      [onCancel, orderId, t]
    );

    if (!show) {
      return null;
    }
    if (step === STEP.reason) {
      return (
        <div className={isHardware ? styles.hardware : styles.normal}>
          <div className={styles.container}>
            <CancelReason
              onBack={() => setStep(STEP.confirm)}
              onConfirm={onConfirm}
              selectedReason={selectedReason}
              setSelectedReason={setSelectedReason}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={isHardware ? styles.hardware : styles.normal}>
        <div className={styles.container}>
          <CancelOrder
            onCancel={onClose}
            onSelectReason={
              isHardware ? () => onConfirm() : () => setStep(STEP.reason)
            }
            cancelFee={cancelFee}
          />
        </div>
      </div>
    );
  }
);

export default Cancel;
